/**
 * Invoice statuses, Explorer buckets and review categories: the frontend mirror
 * of `app/models/enums.py` and `app/services/invoice_buckets.py`.
 *
 * Frozen by S0 (playbook 14 sections 4.3 and 8.1). Ids never change; a label
 * change is a one-line edit here and in `invoice_buckets.py`.
 */

export type InvoiceStatus =
  | 'draft'
  | 'open'
  | 'overdue'
  | 'in_collections'
  | 'deducted'
  | 'pending_payment'
  | 'partially_paid'
  | 'applied'
  | 'written_off'
  | 'void'
  | 'needs_review'
  | 'pending_internal_review'
  | 'pending_customer_response'
  | 'matched'
  | 'rejected'
  | 'on_hold'
  | 'disputed'

export const INVOICE_STATUSES: readonly InvoiceStatus[] = [
  'draft',
  'open',
  'overdue',
  'in_collections',
  'deducted',
  'pending_payment',
  'partially_paid',
  'applied',
  'written_off',
  'void',
  'needs_review',
  'pending_internal_review',
  'pending_customer_response',
  'matched',
  'rejected',
  'on_hold',
  'disputed',
]

/** Tones of the shared StatusChip primitive (stream A). */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

/** Status pill label and tone (labels: plan 04 section 8.3 and plan 05 section 5.4). */
export const STATUS_META: Record<InvoiceStatus, { label: string; tone: StatusTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  needs_review: { label: 'Needs Review', tone: 'warning' },
  pending_internal_review: { label: 'Awaiting approval', tone: 'warning' },
  pending_customer_response: { label: 'Awaiting customer', tone: 'warning' },
  on_hold: { label: 'On Hold', tone: 'warning' },
  matched: { label: 'Ready to Issue', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
  void: { label: 'Void', tone: 'neutral' },
  open: { label: 'Open', tone: 'info' },
  partially_paid: { label: 'Partially Paid', tone: 'info' },
  overdue: { label: 'Overdue', tone: 'warning' },
  in_collections: { label: 'In Collections', tone: 'danger' },
  disputed: { label: 'Disputed', tone: 'danger' },
  deducted: { label: 'Deduction', tone: 'warning' },
  pending_payment: { label: 'Payment Received', tone: 'info' },
  applied: { label: 'Paid in Full', tone: 'success' },
  written_off: { label: 'Written Off', tone: 'neutral' },
}

/** Pill shown instead of the status when `is_duplicate` is true. */
export const DUPLICATE_META = { label: 'Duplicate', tone: 'danger' as StatusTone }

function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Label and tone for any status string; unknown values are title-cased and neutral. */
export function statusMeta(status: string | null | undefined, isDuplicate = false): { label: string; tone: StatusTone } {
  if (isDuplicate) return DUPLICATE_META
  if (!status) return { label: 'Unknown', tone: 'neutral' }
  return STATUS_META[status as InvoiceStatus] ?? { label: titleCase(status), tone: 'neutral' }
}

/** Pre-issue invoices never reach the customer, the portal or receivables totals. */
export const PRE_ISSUE_STATUSES: readonly InvoiceStatus[] = [
  'draft',
  'needs_review',
  'pending_internal_review',
  'pending_customer_response',
  'matched',
  'rejected',
]

/** Issued and still owed (`on_hold` only when the invoice was issued). */
export const OPEN_RECEIVABLE_STATUSES: readonly InvoiceStatus[] = [
  'open',
  'overdue',
  'in_collections',
  'deducted',
  'pending_payment',
  'partially_paid',
  'disputed',
  'on_hold',
]

export const CLOSED_STATUSES: readonly InvoiceStatus[] = ['applied', 'written_off', 'void', 'rejected']

// ---------------------------------------------------------------------------
// Explorer buckets (plan 04 section 1.2)
// ---------------------------------------------------------------------------

export type BucketId =
  | 'all'
  | 'needs_review'
  | 'duplicates'
  | 'rejected'
  | 'matched'
  | 'open'
  | 'overdue'
  | 'disputes'
  | 'paid'

export const BUCKET_IDS: readonly BucketId[] = [
  'all',
  'needs_review',
  'duplicates',
  'rejected',
  'matched',
  'open',
  'overdue',
  'disputes',
  'paid',
]

export const BUCKET_LABELS: Record<BucketId, string> = {
  all: 'All',
  needs_review: 'Needs Review',
  duplicates: 'Duplicates',
  rejected: 'Rejected',
  matched: 'Ready to Issue',
  open: 'Open',
  overdue: 'Overdue',
  disputes: 'Disputes',
  paid: 'Closed',
}

/** Tab badge tone (plan 04 section 1.2): orange for Duplicates, emerald for success tabs. */
export const BUCKET_TONES: Record<BucketId, 'neutral' | 'orange' | 'emerald'> = {
  all: 'neutral',
  needs_review: 'neutral',
  duplicates: 'orange',
  rejected: 'neutral',
  matched: 'emerald',
  open: 'neutral',
  overdue: 'neutral',
  disputes: 'neutral',
  paid: 'emerald',
}

export function isBucketId(value: string | null | undefined): value is BucketId {
  return !!value && (BUCKET_IDS as readonly string[]).includes(value)
}

// ---------------------------------------------------------------------------
// Review categories (plan 04 section 8.4, plus the Workbench write-off flag)
// ---------------------------------------------------------------------------

export type ReviewCategory =
  | 'price_variance'
  | 'quantity_mismatch'
  | 'pod_missing'
  | 'missing_customer_po'
  | 'vat_treatment'
  | 'currency_mismatch'
  | 'so_mismatch'
  | 'credit_limit'
  | 'possible_duplicate'
  | 'customer_dispute'
  | 'short_payment'
  | 'interco_counter_entry'
  | 'ai_flagged'
  | 'write_off_proposal'

/** `label` for chips and rows, `tile` for the queue summary strip. */
export const REVIEW_CATEGORY_META: Record<ReviewCategory, { label: string; tile: string }> = {
  price_variance: { label: 'Price variance', tile: 'Price Variance' },
  quantity_mismatch: { label: 'Quantity vs delivery', tile: 'Qty vs POD' },
  pod_missing: { label: 'Missing proof of delivery', tile: 'Missing POD' },
  missing_customer_po: { label: 'Missing customer PO', tile: 'Missing PO' },
  vat_treatment: { label: 'VAT treatment', tile: 'VAT Treatment' },
  currency_mismatch: { label: 'Currency mismatch', tile: 'Currency' },
  so_mismatch: { label: 'Sales order mismatch', tile: 'SO Mismatch' },
  credit_limit: { label: 'Credit limit', tile: 'Credit Limit' },
  possible_duplicate: { label: 'Possible duplicate', tile: 'Duplicate' },
  customer_dispute: { label: 'Customer dispute', tile: 'Dispute' },
  short_payment: { label: 'Short payment', tile: 'Short Payment' },
  interco_counter_entry: { label: 'Intercompany entry', tile: 'Intercompany' },
  ai_flagged: { label: 'Flagged by AI Employee', tile: 'AI Flagged' },
  write_off_proposal: { label: 'Write-off proposal', tile: 'Write-off' },
}

export function reviewCategoryLabel(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  return REVIEW_CATEGORY_META[value as ReviewCategory]?.label ?? titleCase(value)
}
